import { useState, useEffect } from 'react';
import { Moon, Sun, ArrowLeft, Box, Wrench } from 'lucide-react';
import Editor from './Editor';
import OutputPanel from './OutputPanel';
import DeveloperToolsPanel from './DeveloperToolsPanel';
import { jsonToTypeScript } from '../utils/jsonToTypeScript';
import { jsonToZod } from '../utils/jsonToZod';
import { jsonToOpenAPI } from '../utils/jsonToOpenAPI';
import { compareTokens } from '../utils/tokenCounter';
import { jsonToToon, toonToJson } from '../utils/toonConverter';

const SAMPLE_JSON = `{
  "user": {
    "id": 1,
    "name": "John Doe",
    "email": "john@example.com",
    "isActive": true,
    "roles": ["admin", "user"],
    "metadata": {
      "lastLogin": "2024-01-15T10:30:00Z",
      "loginCount": 42
    }
  },
  "posts": [
    {
      "id": 101,
      "title": "Getting Started with TOON",
      "content": "TOON is a compact data format...",
      "tags": ["tutorial", "beginner"],
      "published": true
    }
  ]
}`;

interface WorkspaceProps {
  onBack: () => void;
  openDevTools?: boolean;
}

function Workspace({ onBack, openDevTools = false }: WorkspaceProps) {
  const [darkMode, setDarkMode] = useState(true);
  const [input, setInput] = useState(SAMPLE_JSON);
  const [inputMode, setInputMode] = useState<'json' | 'toon'>('json');
  const [showDevTools, setShowDevTools] = useState(openDevTools);
  const [outputs, setOutputs] = useState({
    typescript: '',
    zod: '',
    openapi: '',
    toon: '',
    json: '',
    tokens: ''
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    processInput(input);
  }, [input, inputMode]);

  const processInput = (value: string) => {
    if (!value.trim()) {
      setOutputs({
        typescript: '',
        zod: '',
        openapi: '',
        toon: '',
        json: '',
        tokens: ''
      });
      setError(null);
      return;
    }

    try {
      setError(null);

      if (inputMode === 'json') {
        // Validate JSON
        JSON.parse(value);

        // Generate TypeScript
        const ts = jsonToTypeScript(value);

        // Generate Zod
        const zod = jsonToZod(value);

        // Generate OpenAPI
        const openapi = jsonToOpenAPI(value);

        // Generate TOON
        const toon = jsonToToon(value);

        // Calculate tokens
        const tokenComparison = compareTokens(value, toon);
        const tokens = `JSON Tokens: ${tokenComparison.jsonTokens}
TOON Tokens: ${tokenComparison.toonTokens}
Savings: ${tokenComparison.savings} tokens (${tokenComparison.savingsPercent.toFixed(1)}%)

${tokenComparison.savingsPercent > 0 ? '✓ TOON is more compact' : '⚠ TOON is larger (may happen with small data)'}`;

        setOutputs({
          typescript: ts,
          zod,
          openapi,
          toon,
          json: value,
          tokens
        });
      } else {
        // TOON mode - convert to JSON
        const json = toonToJson(value);
        JSON.parse(json); // Validate

        const ts = jsonToTypeScript(json);
        const zod = jsonToZod(json);
        const openapi = jsonToOpenAPI(json);
        const tokenComparison = compareTokens(json, value);
        const tokens = `JSON Tokens: ${tokenComparison.jsonTokens}
TOON Tokens: ${tokenComparison.toonTokens}
Savings: ${tokenComparison.savings} tokens (${tokenComparison.savingsPercent.toFixed(1)}%)`;

        setOutputs({
          typescript: ts,
          zod,
          openapi,
          toon: value,
          json,
          tokens
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid input');
      setOutputs({
        typescript: '',
        zod: '',
        openapi: '',
        toon: '',
        json: '',
        tokens: ''
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] dark:bg-dark-bg transition-colors">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg rounded-lg transition-colors"
              title="Back to home"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-dark-muted" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#FF6B6B] rounded-lg flex items-center justify-center rotate-3">
                <Box className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                  JsonToonCraft
                </div>
                <div className="text-xs text-gray-500 dark:text-dark-muted">
                  Transform data instantly
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDevTools(!showDevTools)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                showDevTools
                  ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-dark-bg text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-dark-border'
              }`}
              title="Toggle Developer Tools"
            >
              <Wrench className="w-4 h-4" />
              <span className="hidden sm:inline">Dev Tools</span>
            </button>

            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg bg-gray-100 dark:bg-dark-bg text-gray-600 dark:text-dark-muted hover:text-gray-900 dark:hover:text-dark-text transition-colors"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="h-[calc(100vh-73px)] flex">
        {showDevTools ? (
          /* Developer Tools Panel - Full Screen Workspace */
          <DeveloperToolsPanel onClose={() => setShowDevTools(false)} />
        ) : (
          <>
            {/* Left Panel - Input */}
            <div className="w-1/2 border-r border-gray-200 dark:border-dark-border">
              <div className="h-full flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-dark-text">Input Mode:</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setInputMode('json')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                          inputMode === 'json'
                            ? 'bg-[#FF6B6B] text-white shadow-lg shadow-[#FF6B6B]/20'
                            : 'bg-gray-100 dark:bg-dark-bg text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-dark-border'
                        }`}
                      >
                        JSON
                      </button>
                      <button
                        onClick={() => setInputMode('toon')}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                          inputMode === 'toon'
                            ? 'bg-[#4ECDC4] text-white shadow-lg shadow-[#4ECDC4]/20'
                            : 'bg-gray-100 dark:bg-dark-bg text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-dark-border'
                        }`}
                      >
                        TOON
                      </button>
                    </div>
                  </div>
                  {error && (
                    <div className="mt-3 text-sm text-[#FF6B6B] bg-[#FFE5D9] dark:bg-red-900/20 px-4 py-2 rounded-lg">
                      {error}
                    </div>
                  )}
                </div>
                <Editor
                  value={input}
                  onChange={setInput}
                  language={inputMode === 'json' ? 'json' : 'plaintext'}
                  darkMode={darkMode}
                />
              </div>
            </div>

            {/* Right Panel - Outputs */}
            <div className="w-1/2">
              <OutputPanel outputs={outputs} darkMode={darkMode} />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Workspace;
